import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, Card, Btn } from '../components/ui';
import { api } from '../lib/api';

interface TransferDetail {
  intent: {
    _id: string;
    reference: string;
    status: string;
    concept: 'ORDER' | 'WALLET_TOPUP';
    method: string;
    amount: number;
    walletAmount: number;
    currency: string;
    provider: string;
    externalId?: string | null;
    paymentLink?: string | null;
    orderId?: string | null;
    orderIds?: string[];
    expiresAt: string;
    createdAt: string;
    updatedAt: string;
  };
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    walletBalance: number;
    walletStatus?: string;
    verified: boolean;
    banned?: boolean;
    initials?: string;
  };
  orders: Array<{
    id: string;
    code: string;
    status: string;
    fuelVariantName: string;
    fuelOctane: string;
    liters: number;
    total: number;
    stationName: string;
    slotDate: string;
    slotTime: string;
    slotPump: string;
  }>;
}

export default function TransferDetailPage() {
  const { reference } = useParams<{ reference: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<TransferDetail | { error: string }>({
    queryKey: ['admin', 'transfer', reference],
    queryFn: () => api(`/api/admin/bank-transfers/${reference}`),
    enabled: !!reference,
  });

  const markPaid = useMutation({
    mutationFn: () => api(`/api/admin/bank-transfers/${reference}/mark-paid`, { body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'transfer', reference] });
      qc.invalidateQueries({ queryKey: ['admin', 'transfers'] });
    },
  });

  if (isLoading) return <div style={{ padding: 40, color: 'var(--text-3)' }}>Cargando…</div>;
  if (!data || 'error' in data) {
    return (
      <div>
        <PageHeader kicker="TRANSFERENCIA" title="No encontrada" />
        <Link to="/transfers" style={{ color: 'var(--accent)', textDecoration: 'none' }}>← Volver a la lista</Link>
      </div>
    );
  }

  const { intent, user, orders } = data;
  const isPending = intent.status === 'pending';
  const expiresAt = new Date(intent.expiresAt);
  const expired = expiresAt.getTime() < Date.now();
  const totalAmount = intent.amount + intent.walletAmount;

  return (
    <div>
      <Link to="/transfers" style={{ color: 'var(--text-2)', fontSize: 13, textDecoration: 'none' }}>← Transferencias</Link>
      <PageHeader
        kicker={`TRANSFERENCIA · ${intent.status.toUpperCase()}`}
        title={intent.reference}
        sub={`${intent.concept === 'WALLET_TOPUP' ? 'Recarga de billetera' : 'Pago de orden'} · ${user.firstName} ${user.lastName} (${user.email})`}
        action={
          isPending && !expired ? (
            <Btn onClick={() => { if (confirm(`Confirmar recepción de $${totalAmount.toFixed(2)} ${intent.currency}?`)) markPaid.mutate(); }} disabled={markPaid.isPending}>
              {markPaid.isPending ? '…' : 'Marcar recibida'}
            </Btn>
          ) : null
        }
      />

      {expired && isPending && (
        <Card style={{ marginTop: 16, borderColor: 'var(--danger)' }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--danger)' }}>EXPIRADA</div>
          <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-2)' }}>
            El plazo de pago venció el {expiresAt.toLocaleString('es-MX')}. Recibir esta transferencia ahora obligaría a reabrir las órdenes; mejor pedir al usuario que cree una nueva.
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 20 }}>
        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>INTENT</div>
          <table style={{ width: '100%', marginTop: 12, fontSize: 13 }}>
            <tbody>
              <Row k="Referencia" v={<span className="mono">{intent.reference}</span>} />
              <Row k="Estado" v={<span className="mono" style={{ fontWeight: 600 }}>{intent.status.toUpperCase()}</span>} />
              <Row k="Concepto" v={intent.concept === 'WALLET_TOPUP' ? 'Recarga wallet' : 'Pago de orden'} />
              <Row k="Método" v={<span className="mono">{intent.method.toUpperCase()}</span>} />
              <Row k="Proveedor" v={<span className="mono">{intent.provider}</span>} />
              {intent.externalId && <Row k="ID externo" v={<span className="mono" style={{ fontSize: 11 }}>{intent.externalId}</span>} />}
              <Row k="Monto" v={<span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>${intent.amount.toFixed(2)} {intent.currency}</span>} />
              {intent.walletAmount > 0 && (
                <Row k="De wallet" v={<span className="mono">${intent.walletAmount.toFixed(2)}</span>} />
              )}
              {intent.walletAmount > 0 && (
                <Row k="Total combinado" v={<span className="mono" style={{ fontWeight: 600 }}>${totalAmount.toFixed(2)}</span>} />
              )}
              <Row k="Creada" v={<span className="mono">{new Date(intent.createdAt).toLocaleString('es-MX')}</span>} />
              <Row k="Expira" v={<span className="mono" style={{ color: expired ? 'var(--danger)' : 'var(--text)' }}>{expiresAt.toLocaleString('es-MX')}</span>} />
              {intent.paymentLink && (
                <Row k="Link pago" v={<a href={intent.paymentLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Abrir →</a>} />
              )}
            </tbody>
          </table>
        </Card>

        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>USUARIO</div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Link to={`/users/${user.id}`} style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 16, fontWeight: 600 }}>
              {user.firstName} {user.lastName} →
            </Link>
            <span style={{ color: 'var(--text-2)', fontSize: 13 }}>{user.email}</span>
          </div>
          <table style={{ width: '100%', marginTop: 14, fontSize: 13 }}>
            <tbody>
              <Row k="Rol" v={<span className="mono">{user.role.toUpperCase()}</span>} />
              <Row k="Verificado" v={user.verified ? '✓' : 'No'} />
              {user.banned && <Row k="Estado" v={<span style={{ color: 'var(--danger)' }}>BANEADO</span>} />}
              <Row k="Saldo wallet" v={<span className="mono">${user.walletBalance.toFixed(2)}</span>} />
              {user.walletStatus && user.walletStatus !== 'active' && (
                <Row k="Wallet status" v={<span className="mono" style={{ color: 'var(--danger)' }}>{user.walletStatus.toUpperCase()}</span>} />
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.14em', marginBottom: 10 }}>
          {orders.length === 0 ? 'SIN ÓRDENES LIGADAS' : `ÓRDENES LIGADAS (${orders.length})`}
        </div>
        {orders.length === 0 ? (
          <Card>
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>
              {intent.concept === 'WALLET_TOPUP'
                ? 'Esta transferencia es una recarga de wallet — no se asocia a una orden concreta.'
                : 'Sin órdenes asociadas (posiblemente cancelada antes de crear las órdenes).'}
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/orders/${o.id}`}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '14px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{o.code}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
                    {o.fuelVariantName} {o.fuelOctane} · {o.liters} L · {o.stationName}
                  </div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                    {o.slotDate} {o.slotTime} · Bomba {o.slotPump}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontWeight: 600, fontSize: 16 }}>${o.total.toFixed(2)}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {o.status}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr>
      <td style={{ padding: '6px 0', color: 'var(--text-3)', fontSize: 11, width: 140, verticalAlign: 'top' }}>{k}</td>
      <td style={{ padding: '6px 0', fontSize: 13 }}>{v}</td>
    </tr>
  );
}
